import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, TicketIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { NAV_LINKS, EVENTS } from '../data/site';
import { LOGO_ROCKET } from './logos';
const isExternal = (href: string) => href.startsWith('http');

/*  NAV OVERFLOW LAW  ───────────────────────────────────────────────
    Ryan caught the top bar being CUT OFF when the window narrows — the
    GET TICKETS pill disappeared off the right edge.

    Cause: the seven nav labels are long ("FUTURE ASTRONAUT RECORDS" is 24
    characters on its own). MEASURED intrinsic widths at the sizing below:

        logo + wordmark   180px
        seven links       916px
        GET TICKETS       145px
        rocket + gaps + side padding  138px
        ──────────────────────────────
        TOTAL           1,379px   — wider than the max-w-7xl (1280px) shell

    And because v13 put `overflow-x: clip` on html/.app-shell (the change
    that finally made scroll-snap engage), that overflow CANNOT scroll. It
    is silently clipped at the right edge, taking the button with it.

    Fix: the full link row appears only at 1400px, where it measurably
    fits. Below that the hamburger handles it. The old breakpoint was `lg`
    (1024px), which is why the clipping started so early.

    ⚠ If a nav label is added or lengthened, RE-MEASURE. The row must stay
    under 1400px of intrinsic width or it clips again with no scrollbar to
    warn you. Shortening "Future Astronaut Records" → "Records" and "Event
    Production" → "Production" brings the total to 1,191px, which would let
    this drop back to a 1280px breakpoint — that is a copy decision, not a
    layout one.
    ───────────────────────────────────────────────────────────────── */

const DESKTOP_NAV = 1400;
export function Navbar() {
  const [condensed, setCondensed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // close the panel if the viewport grows into the desktop nav
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_NAV}px)`);
    const onChange = () => {
      if (mq.matches) setOpen(false);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return <header className="fixed inset-x-0 top-0 z-50" data-id="element-23" data-mp="2dff8uve2ouu6">
      <div className={`transition-[background-color,border-color,backdrop-filter,padding] duration-300 ease-expo ${condensed ? 'border-b border-white/10 bg-void/80 py-2.5 backdrop-blur-xl' : 'border-b border-transparent bg-transparent py-5'}`} data-id="element-24" data-mp="2sav4h4uawyso">
        <nav aria-label="Primary" className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-5 sm:px-8 min-[1400px]:gap-4" data-id="element-25" data-mp="2k80twe31kpi">
          <a href="#top" className="group flex shrink-0 items-center gap-2.5 font-display text-[0.65rem] tracking-[0.18em] text-white sm:text-xs" data-id="element-26" data-mp="f3luzruj50al">
            <img src={LOGO_ROCKET} alt="" aria-hidden="true" className="h-8 w-auto transition-transform duration-300 ease-expo group-hover:-translate-y-0.5" data-id="element-27" data-mp="55piowpuvzaw" />
            <span className="whitespace-nowrap" data-id="element-28" data-mp="xuwj618veyys">FUTURE ASTRONAUT</span>
          </a>

          <ul className="hidden items-center gap-0.5 min-[1400px]:flex" data-id="element-29" data-mp="k383jnzexsd2">
            {NAV_LINKS.map(link => <li key={link.label} data-id="element-30" data-mp="o3s28vzmx41b">
                <a href={link.href} target={isExternal(link.href) ? '_blank' : undefined} rel={isExternal(link.href) ? 'noopener noreferrer' : undefined} className="relative block whitespace-nowrap rounded-full px-2.5 py-2 font-tech text-[0.72rem] font-medium tracking-[0.08em] text-white/60 transition-colors duration-200 ease-out hover:text-white" data-id="element-31" data-mp="o30e268da8l2">
                  {link.label.toUpperCase()}
                </a>
              </li>)}
          </ul>

          <div className="flex shrink-0 items-center gap-2" data-id="element-32" data-mp="3b2nwm4y9fw5o">
            <a href={EVENTS[0].tickets} target="_blank" rel="noopener noreferrer" className="group hidden shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-violet px-4 py-2.5 font-tech text-[0.72rem] font-semibold tracking-[0.1em] text-white shadow-neon transition-[transform,box-shadow,background-color] duration-200 ease-expo hover:scale-[1.03] hover:bg-violet-soft sm:flex min-[1400px]:px-5" data-id="element-33" data-mp="3b48ua3fvfv7r">
              <TicketIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-34" data-mp="uf39gl4g2wwz" />
              GET TICKETS
            </a>
            <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'} className="glass flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-colors duration-200 ease-out hover:border-violet/60 min-[1400px]:hidden" data-id="element-35" data-mp="pkpak8qkq7nk">
              {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-36" data-mp="2r8foio05ly0k" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-37" data-mp="2r6la7sje145g" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence data-id="element-38" data-mp="30xz5tp87xjtb">
        {open ? <motion.div initial={{
        opacity: 0,
        y: -12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -12
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="mx-4 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-void/95 p-3 backdrop-blur-xl min-[1400px]:hidden" data-id="element-39" data-mp="310bvypo4r0hg">
            <ul className="flex flex-col" data-id="element-40" data-mp="1ewitgv6vsshp">
              {NAV_LINKS.map(link => <li key={link.label} data-id="element-41" data-mp="19xpdmw44gped">
                  <a href={link.href} target={isExternal(link.href) ? '_blank' : undefined} rel={isExternal(link.href) ? 'noopener noreferrer' : undefined} onClick={() => setOpen(false)} className="block rounded-xl px-4 py-3 font-tech text-sm tracking-[0.14em] text-white/75 transition-colors duration-200 ease-out hover:bg-white/5 hover:text-white" data-id="element-42" data-mp="19ul1nckf0pd5">
                    {link.label.toUpperCase()}
                  </a>
                </li>)}
            </ul>
            <a href={EVENTS[0].tickets} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet px-5 py-3 font-tech text-sm font-semibold tracking-[0.14em] text-white sm:hidden" data-id="element-43" data-mp="22m4nl48echiu">
              <TicketIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-44" data-mp="2otqw23baef6m" />
              GET TICKETS
            </a>
          </motion.div> : null}
      </AnimatePresence>
    </header>;
}